import { defer, EMPTY, expand, from, Observable, scan, takeWhile } from 'rxjs';
import { concatMap, filter, map } from 'rxjs/operators';
import { AiResponseEvent } from '../../domain/global-ai/utils';

/**
 * Creates an observable that processes Server-Sent Events (SSE) from a readable stream
 * and emits parsed AiResponseEvent objects.
 *
 * @param reader - The reader instance of the readable stream.
 * @param decoder - The TextDecoder used to decode stream chunks.
 * @returns Observable<AiResponseEvent> emitting parsed SSE events.
 */
export const processSseStream = (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder
): Observable<AiResponseEvent> => {
  return defer(() => from(reader.read())).pipe(
    // recursively read from the stream until done https://developer.mozilla.org/en-US/docs/Web/API/ReadableStreamDefaultReader/read)
    expand((result) => (result.done ? EMPTY : from(reader.read()))),
    // continue emitting until a done result is received.
    takeWhile((result) => !result.done, true),
    // decode chunk to text
    map((result) => (result.value ? decoder.decode(result.value, { stream: true }) : '')),
    // accumulate chunks
    scan<string, { buffer: string; events: AiResponseEvent[] }>(
      (acc, chunk) => {
        const fullText = acc.buffer + chunk;
        const lines = fullText.split('\n');
        const events: AiResponseEvent[] = [];

        // go through all lines to get AiResponseEvents
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          // each sse is being sent as 'data: xxx'
          if (line.startsWith('data:')) {
            const jsonString = line.replace('data:', '').trim();
            if (jsonString) {
              try {
                events.push(JSON.parse(jsonString) as AiResponseEvent);
              } catch (err) {
                throw new Error(`Failed to parse SSE JSON: ${err}`);
              }
            }
          }
        }

        // return the remaining potentially incomplete line as buffer
        return {
          buffer: lines[lines.length - 1],
          events,
        };
      },
      { buffer: '', events: [] }
    ),
    // emit each parsed event separately
    concatMap((result) => from(result.events.length ? result.events : [])),
    // filter out any empty events
    filter((event) => Boolean(event))
  );
};

/**
 * Creates an observable that emits decoded text chunks from a readable stream.
 *
 * @param reader - The reader instance of the readable stream.
 * @param decoder - The TextDecoder used to decode stream chunks.
 * @returns Observable<string> emitting decoded stream chunks.
 */
export const processStream = (
  reader: ReadableStreamDefaultReader,
  decoder: TextDecoder
): Observable<string> => {
  return defer(() => from(reader.read())).pipe(
    // recursively read from the stream until done https://developer.mozilla.org/en-US/docs/Web/API/ReadableStreamDefaultReader/read)
    expand((result) => (result.done ? EMPTY : from(reader.read()))),
    // continue emitting until a done result is received.
    takeWhile((result) => !result.done, true),
    // decode the ArrayBuffer to a string
    map((result) => decoder.decode(result.value as AllowSharedBufferSource, { stream: true })),
    // emit only non-empty, trimmed strings.
    filter((decoded) => decoded.trim().length > 0),
  );
};
